import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Button, Table, Modal } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import {
  addBulkDirectory,
  addDirectory,
} from "../../../Redux/Directory/action";
import { BsUpload } from "react-icons/bs";
import * as XLSX from "xlsx";
import "../../../assets/css/directory.css";
import BulkAddDirectoryReportModal from "./bulkAddDirectoryReportModal";

const BulkAddDirectoryModal = ({ show, onHide,setBulkAddModal }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.authReducer?.data);
  const accountID = user?.accountID;
  const [directoryData, setDirectoryData] = useState({
    name: "",
    email: "",
    phone: "",
    department: "",
  });
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkAddDirectoryReportModal, setShowBulkAddDirectoryReportModal] =
    useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [bulkUploadLength, setBulkUploadLength] = useState(0);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setError("Unsupported file format. Supported formats are CSV and .xlsx");
    } else {
      setFile(acceptedFiles[0]);
      setError("");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
        ".xlsm",
        ".xls",
      ],
    },
  });

  const handleFileRead = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const binaryStr = event.target.result;
      let jsonData = [];

      if (file.type === "text/csv") {
        const csvData = binaryStr.split("\n").map((row) => row.split(","));
        jsonData = csvData.map((row) => row.map((cell) => cell.trim()));
      } else {
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      }

      // Normalize headers to lowercase
      const headers = jsonData[0].map((header) => header.trim().toLowerCase());
      const requiredHeaders = ["name", "email", "phone", "department"];
      const isValid = requiredHeaders.every((header) =>
        headers.includes(header),
      );

      if (!isValid) {
        setError(
          "The selected file does not meet the criteria. Please ensure it contains the columns: Name, Email, Phone, and Department.",
        );
        setData([]);
        return;
      }

      // Find the index of each required header
      const headerIndex = {
        name: headers.indexOf("name"),
        email: headers.indexOf("email"),
        phone: headers.indexOf("phone"),
        department: headers.indexOf("department"),
      };

      const mappedData = jsonData.slice(1).map((row) => ({
        name: row[headerIndex.name]?.trim(),
        email: row[headerIndex.email]?.trim(),
        phone: row[headerIndex.phone]?.trim(),
        department: row[headerIndex.department]?.trim(),
      }));

      const filteredData = mappedData.filter((item) => {
        return Object.values(item).every(
          (value) => value !== undefined && value !== "",
        );
      });

      setData(filteredData);
    };

    if (file.type === "text/csv") {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  useEffect(() => {
    if (file) {
      handleFileRead(file);
    }
  }, [file]);

  let Data;
  const handleSubmit = async (event) => {
    Data = data.map((item) => {
      return {
        ...item,
        department: item.department.toUpperCase(),
        phone: item.phone.replace(/\D+/g, ""),
        accountID: accountID,
      };
    });

    setBulkUploadLength(Data?.length);
    const onProgress = (progressEvent) => {
      const progress = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total,
      );
      setUploadProgress(progress);
    };

    setShowBulkAddDirectoryReportModal(true);
    setBulkAddModal(false)

    await addBulkDirectory(dispatch, Data, accountID, onProgress);
  };

  const handleClose = () => {
    setFile(null);
    setData([]);
    setError("");
    onHide();
  };

  return (
    <Container fluid>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-center main-h4-tag-large w-100" >
            Add Contacts in Bulk
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row className="text-center mb-4 ">
              <Col>
                <small style={{ color: "grey" }}>
                  Upload an Excel or CSV file to add multiple contacts at once.
                  Review the contacts before confirming.
                </small>
              </Col>
            </Row>
            <Row className="flex-column justify-content-center">
              <Col className="d-flex justify-content-center">
                <div
                  {...getRootProps({ className: "dropzone " })}
                  className={`p-3 border-dashed w-25 m-1 text-center ${
                    isDragActive ? "border-primary" : "border-secondary"
                  }`}
                  style={{ cursor: "pointer" }}
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p className="text-primary ">Drop the files here...</p>
                  ) : (
                    <>
                      <div className="d-flex justify-content-center mb-3 ">
                        <BsUpload size={25} color="grey" />
                      </div>
                      <div className="d-flex justify-content-center">
                        <small style={{ color: "grey" }}>
                          Drag and drop your Excel or CSV file here, or click to
                          upload
                        </small>
                      </div>
                    </>
                  )}
                </div>
                {file && (
                  <div className="w-25 border-dashed d-flex flex-column justify-content-center align-items-center m-1">
                    <strong >Selected file</strong>
                    <small className="text-center mt-4">{file.name}</small>
                  </div>
                )}
              </Col>
              <div className="d-flex justify-content-center">
                {error && (
                  <div className="mt-2 text-center w-50 ">
                    <small style={{ color: "red" }}>{error}</small>
                  </div>
                )}
              </div>
            </Row>
            {data.length ? (
              <Row className="justify-content-center mt-4">
                <Col md={12}>
                  <div className="rounded border bg-light p-4 shadow-sm">
                    <div className="text-center mb-3">
                      <h2 className="main-h4-tag-large">Contact Preview</h2>
                    </div>

                    <div className="scrollable-table-container">
                      <Table bordered hover>
                        <thead className={"text-center"}>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Department</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data?.map((item, index) => (
                            <tr key={index}>
                              <td className="w-25 text-center">{item?.name}</td>
                              <td className="w-25 text-center">
                                {item?.email}
                              </td>
                              <td className="w-25 text-center">
                                {item?.phone}
                              </td>
                              <td className="w-25 text-center">
                                {item?.department}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>
            ) : (
              ""
            )}
          </Container>
        </Modal.Body>
        {data.length ? (
          <Modal.Footer>
            <div className={"w-100 d-flex justify-content-between"}>
              <Button className="custom-button" onClick={handleClose}>
                Close
              </Button>
              <Button className="custom-button" onClick={handleSubmit}>
                Upload
              </Button>
            </div>
          </Modal.Footer>
        ) : (
          ""
        )}
      </Modal>
      <BulkAddDirectoryReportModal
        show={bulkAddDirectoryReportModal}
        progress={uploadProgress}
        dataLength={bulkUploadLength}
        setShowBulkAddDirectoryReportModal={setShowBulkAddDirectoryReportModal}
      />{" "}
    </Container>
  );
};

export default BulkAddDirectoryModal;
